import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, KeyRound, Mail } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export default function AdminLogin() {
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const callAdminAuth = async (body: Record<string, string>) => {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const handleStep1 = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await callAdminAuth({
        action: 'request-code',
        email,
        password,
        security_code: securityCode,
      });
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await callAdminAuth({
        action: 'verify-code',
        email,
        code: verificationCode,
      });
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) throw signInError;
      navigate('/04062001/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--dash-border)',
    background: 'var(--dash-input)',
    color: 'var(--dash-text-secondary)',
  };

  return (
    <div
      className="dash-theme min-h-screen flex items-center justify-center px-6"
      style={{ background: 'var(--dash-bg)' }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link to="/" className="font-mono tracking-widest text-sm font-medium uppercase" style={{ color: 'var(--dash-text-primary)' }}>
            BONESPAY
          </Link>
          <div className="flex items-center justify-center gap-2 mt-3">
            <span
              className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{
                color: 'var(--dash-text-dim)',
                border: '1px solid var(--dash-border)',
                background: 'var(--dash-hover-bg)',
              }}
            >
              Admin
            </span>
            <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Platform Administration</p>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-8">
          <div className="flex-1 h-px" style={{ background: step >= 1 ? 'var(--dash-text-faint)' : 'var(--dash-border)' }} />
          <span
            className="font-mono text-[10px] uppercase tracking-widest"
            style={{ color: step === 1 ? 'var(--dash-text-muted)' : 'var(--dash-text-faint)' }}
          >
            Credentials
          </span>
          <div className="flex-1 h-px" style={{ background: step >= 2 ? 'var(--dash-text-faint)' : 'var(--dash-border)' }} />
          <span
            className="font-mono text-[10px] uppercase tracking-widest"
            style={{ color: step === 2 ? 'var(--dash-text-muted)' : 'var(--dash-text-faint)' }}
          >
            Email Code
          </span>
          <div className="flex-1 h-px" style={{ background: 'var(--dash-border)' }} />
        </div>

        {error && (
          <div className="border border-red-500/30 bg-red-500/10 rounded px-4 py-3 mb-5">
            <p className="font-sans text-sm text-red-300">{error}</p>
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={handleStep1} className="space-y-5">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--dash-text-dim)' }}>Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@bonespay.com"
                className="w-full rounded px-4 py-3 font-sans text-sm focus:outline-none transition-colors"
                style={{ ...inputStyle, '--placeholder-color': 'var(--dash-text-faint)' } as React.CSSProperties}
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--dash-text-dim)' }}>Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded px-4 py-3 font-sans text-sm focus:outline-none transition-colors"
                style={inputStyle}
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--dash-text-dim)' }}>
                <span className="flex items-center gap-1.5">
                  <KeyRound size={10} />
                  Security Code
                </span>
              </label>
              <input
                type="password"
                required
                value={securityCode}
                onChange={(e) => setSecurityCode(e.target.value)}
                placeholder="Enter admin secret"
                className="w-full rounded px-4 py-3 font-sans text-sm focus:outline-none transition-colors"
                style={inputStyle}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-zinc-900 font-mono text-sm font-medium py-3.5 rounded hover:bg-zinc-200 transition-colors disabled:opacity-50 uppercase tracking-wider flex items-center justify-center gap-2 group"
            >
              {loading ? (
                'Verifying...'
              ) : (
                <>
                  Verify Identity
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleStep2} className="space-y-5">
            <div
              className="rounded px-4 py-3 flex items-start gap-3"
              style={{
                border: '1px solid var(--dash-border)',
                background: 'var(--dash-input)',
              }}
            >
              <Mail size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--dash-text-dim)' }} />
              <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>
                A verification code has been sent to{' '}
                <span style={{ color: 'var(--dash-text-secondary)' }}>{email}</span>
              </p>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--dash-text-dim)' }}>Verification Code</label>
              <input
                type="text"
                required
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="w-full rounded px-4 py-3 font-sans text-sm focus:outline-none transition-colors text-center tracking-[0.3em] text-lg"
                style={inputStyle}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-zinc-900 font-mono text-sm font-medium py-3.5 rounded hover:bg-zinc-200 transition-colors disabled:opacity-50 uppercase tracking-wider flex items-center justify-center gap-2 group"
            >
              {loading ? (
                'Completing...'
              ) : (
                <>
                  Complete Login
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setError('');
              }}
              className="w-full font-mono text-xs transition-colors uppercase tracking-wider py-2"
              style={{ color: 'var(--dash-text-faint)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-faint)'; }}
            >
              Back to credentials
            </button>
          </form>
        )}

        <div className="mt-10 pt-6" style={{ borderTop: '1px solid var(--dash-border-light)' }}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-center" style={{ color: 'var(--dash-text-faint)' }}>
            Authorized access only.
          </p>
        </div>
      </div>
    </div>
  );
}
